import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { GitMerge } from "lucide-react";
import { StockCount } from "@/types/general";
import StockCountStatusChip from "./StockCountStatusChip";
import StockCountLocations from "./StockCountLocations";

/**
 * The connector is pinned to the first text line rather than to the middle of the row, so it stays
 * put when a long origin wraps onto a second line. Everything it draws derives from these two.
 */
const ROW_PADDING_Y = 8;
const LINE_HEIGHT = 24;
const CONNECTOR_Y = ROW_PADDING_Y + LINE_HEIGHT / 2;
const DOT_SIZE = 7;

/**
 * How many levels of branches to draw. Merges can nest without limit, but each level costs 24px of
 * indent, and past this depth the branches are more clutter than information — so a merge sitting
 * at the bottom level just names its origins inline instead of opening another branch.
 */
const MAX_TREE_DEPTH = 8;

/**
 * Git-like visualisation of the stock counts a merge was built from.
 *
 * Because origins are shown as one-line summaries rather than full product tables, there is room
 * to lay out the ramification rather than truncate it at the first level.
 */
export default function MergeOriginsTree({
    origins,
}: {
    origins: StockCount.Components.MergeItem[];
}) {
    const { t } = useTranslation("", { keyPrefix: "inventory.stockCount.main.merge.tree" });

    return (
        // Same white card as the entries table: it groups the tree and gives the branch lines
        // enough contrast, which they lack against the collapse's lavender fill.
        <div className="space-y-[8px] rounded-lg border border-solid border-[#B3B5EB] bg-white p-[16px]">
            <div className="flex items-center gap-[8px] font-semibold text-accent">
                <GitMerge size={16} />
                <span>{t("heading", { count: origins.length })}</span>
            </div>

            <MergeOriginsBranch origins={origins} depth={1} />
        </div>
    );
}

function MergeOriginsBranch({
    origins,
    depth,
}: {
    origins: StockCount.Components.MergeItem[];
    depth: number;
}) {
    return (
        <div>
            {origins.map((origin, index) => (
                <MergeOriginNode
                    key={origin.id}
                    origin={origin}
                    depth={depth}
                    isLast={index === origins.length - 1}
                />
            ))}
        </div>
    );
}

function MergeOriginNode({
    origin,
    depth,
    isLast,
}: {
    origin: StockCount.Components.MergeItem;
    depth: number;
    isLast: boolean;
}) {
    const { t } = useTranslation("", { keyPrefix: "inventory.stockCount.main.merge.tree" });

    const canBranchFurther = depth < MAX_TREE_DEPTH;

    return (
        <div className="relative pl-[24px]">
            {/*
                Trunk. On a non-last node it runs to the bottom of the whole node — including any
                nested branch — so it passes the children on its way to the next sibling.
                On the last node it stops at the elbow.
            */}
            <div
                className="absolute left-[7px] top-0 w-px bg-[#B3B5EB]"
                style={isLast ? { height: CONNECTOR_Y } : { bottom: 0 }}
            />
            {/* Elbow */}
            <div
                className="absolute left-[7px] h-px w-[14px] bg-[#B3B5EB]"
                style={{ top: CONNECTOR_Y }}
            />
            {/* Node */}
            <div
                className="absolute left-[4px] rounded-full bg-[#7556FA]"
                style={{
                    top: CONNECTOR_Y - DOT_SIZE / 2,
                    height: DOT_SIZE,
                    width: DOT_SIZE,
                }}
            />

            {/*
                Read as a sentence rather than a row of columns: a tree can never align its
                fields into columns anyway, since every nesting level starts at a different indent.
                The connectives stay grey and light so the values carry the line.
            */}
            <div
                className="text-[14px] text-gray-500"
                style={{
                    paddingTop: ROW_PADDING_Y,
                    paddingBottom: ROW_PADDING_Y,
                    lineHeight: `${LINE_HEIGHT}px`,
                }}
            >
                <Trans
                    t={t}
                    i18nKey={origin.user ? "origin" : "originWithoutWorker"}
                    values={{
                        code: origin.code,
                        products: t("products", { count: origin.entries.length }),
                        worker: origin.user
                            ? `${origin.user.first_name} ${origin.user.last_name}`
                            : "",
                        date: origin.date.format("DD.MM.YYYY"),
                    }}
                    components={{
                        code: <span className="bold-highlight" />,
                        value: <span className="font-medium text-accent" />,
                        location: (
                            <StockCountLocations
                                entries={origin.entries}
                                className="font-medium text-accent"
                            />
                        ),
                    }}
                />
                <span> · </span>
                <StockCountStatusChip status={origin.status} asDot />
            </div>

            {origin.is_merge &&
                (canBranchFurther ? (
                    <MergeOriginsBranch origins={origin.included_in_merge} depth={depth + 1} />
                ) : (
                    <div className="pb-[8px] text-[13px] text-gray-500">
                        {t("nestedOrigins")}:{" "}
                        <span className="font-medium text-accent">
                            {origin.included_in_merge.map((nested) => nested.code).join(", ")}
                        </span>
                    </div>
                ))}
        </div>
    );
}
